'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * 주어진 콜백 함수를 디바운스하여, 지정된 지연 시간 이후에만 실행되도록 합니다.
 * 연속된 호출이 있을 때 마지막 호출만 실행하고 이전 호출들은 취소합니다.
 *
 * @param callback 디바운스할 콜백 함수
 * @param delay 디바운스 지연 시간 (밀리초 단위)
 * @returns 디바운스된 콜백 함수
 */
export function useDebounceCallback<TArgs extends readonly unknown[], TReturn>(
  callback: (...args: TArgs) => TReturn,
  delay: number,
): (...args: TArgs) => void {
  const callbackRef = useRef<(...args: TArgs) => TReturn>(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  return useCallback(
    (...args: TArgs) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        callbackRef.current(...args);
      }, delay);
    },
    [delay],
  );
}

/**
 * 입력값을 즉시 반영하고, 디바운스된 값만 외부로 전달하는 훅입니다.
 * 사용자가 타이핑할 때 자연스럽게 입력이 반영되면서, 실제 값 변경은 지연됩니다.
 *
 * @param initialValue 초기값
 * @param onChange 외부로 전달할 디바운스된 콜백 함수
 * @param delay 디바운스 지연 시간 (밀리초 단위, 기본값: 300)
 * @returns [로컬값, 값 변경 핸들러, 외부값 동기화 함수]
 */
export function useDebouncedInput<T>(
  initialValue: T,
  onChange: (value: T) => void,
  delay: number = 300,
): [T, (value: T) => void, (value: T) => void] {
  const [localValue, setLocalValue] = useState<T>(initialValue);

  // 디바운스된 값만 외부로 전달
  const debouncedOnChange = useDebounceCallback((value: T) => {
    onChange(value);
  }, delay);

  // 외부에서 값이 변경되면 로컬 상태도 동기화 (예: 초기화)
  const syncExternalValue = useCallback((value: T) => {
    setLocalValue(value);
  }, []);

  // 값 변경 핸들러
  const handleValueChange = useCallback(
    (value: T) => {
      // 즉시 로컬 상태 업데이트 (자연스러운 입력)
      setLocalValue(value);
      // 디바운스된 값만 외부로 전달
      debouncedOnChange(value);
    },
    [debouncedOnChange],
  );

  // 초기값이 변경되면 로컬 상태도 동기화
  useEffect(() => {
    setLocalValue(initialValue);
  }, [initialValue]);

  return [localValue, handleValueChange, syncExternalValue];
}
